import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

function PageHero() {
  return (
    <section className="relative min-h-[60vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]" />
      <div className="absolute top-10 right-10 w-80 h-80 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-purple-light/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-20 pt-40">
        <span className="hero-badge inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          Client Payments &amp; Mission Giving
        </span>
        <h1 className="hero-line-1 font-heading text-white text-6xl lg:text-7xl font-black mb-4">
          Work With Us
        </h1>
        <p className="hero-sub font-body text-white/75 text-xl max-w-2xl">
          A simple and secure way to make payments or support the mission of Creative Institute.
        </p>
      </div>
    </section>
  );
}

function PaymentMissionSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section ref={ref} className="py-28 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#EEF2FF] rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FEF9C3] rounded-full blur-3xl opacity-40 pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-16`}>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4">
            How Would You Like to Connect?
          </h2>
          <p className="font-body text-gray-500 text-base max-w-xl mx-auto">
            Select one of the options below to take action. We make it simple, professional, and secure.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
          {/* Make a Payment Card */}
          <div className={`scroll-hidden slide-left ${isVisible ? 'scroll-visible' : ''} rounded-3xl overflow-hidden border-2 border-[#C7D2FE] card-lift`}>
            <div className="bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] p-10 text-white">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/15 mb-6">
                <i className="ri-secure-payment-line text-3xl" />
              </div>
              <h3 className="font-heading text-3xl font-black mb-3">Make a Payment</h3>
              <p className="font-body text-white/80 text-base leading-relaxed">
                For program enrollment, services, and invoices.
              </p>
            </div>
            <div className="bg-white p-10">
              <p className="font-body text-gray-600 text-base leading-loose mb-6">
                Creative Institute provides a simple and secure way for clients to make payments. Whether it is for enrollment, a monthly tuition, a studio session, or a service invoice — we will send you a secure payment link directly.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Program enrollment & registration fees',
                  'Monthly tuition payments',
                  'Recording studio sessions',
                  'Off-site service invoices',
                  'Custom payment amounts',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 font-body text-sm text-gray-600">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0">
                      <i className="ri-check-line text-xs" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="font-body text-gray-500 text-sm leading-relaxed mb-6 italic">
                Contact us and our team will send you a secure payment link within 24 hours.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:9102424567"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity"
                >
                  <i className="ri-phone-line" /> Call to Pay
                </a>
                <a
                  href="mailto:info@creativeinstitutellc.com"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-brand-purple text-brand-purple font-body font-semibold text-sm cursor-pointer hover:bg-brand-purple hover:text-white transition-colors"
                >
                  <i className="ri-mail-line" /> Email for Invoice
                </a>
              </div>
            </div>
          </div>

          {/* Mission Giving Card */}
          <div className={`scroll-hidden slide-right ${isVisible ? 'scroll-visible' : ''} rounded-3xl overflow-hidden border-2 border-brand-yellow/40 card-lift`}>
            <div className="bg-gradient-to-br from-[#FEF9C3] to-[#FEF3C7] p-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow mb-6">
                <i className="ri-heart-3-line text-3xl text-brand-blue-dark" />
              </div>
              <h3 className="font-heading text-brand-blue-dark text-3xl font-black mb-3">Mission Giving</h3>
              <p className="font-body text-gray-700 text-base leading-relaxed">
                Support the mission to develop and empower creatives everywhere.
              </p>
            </div>
            <div className="bg-white p-10">
              <p className="font-body text-gray-600 text-base leading-loose mb-6">
                Support Creative Institute&apos;s mission to develop and empower creatives in an inclusive environment where every creative mind matters.
              </p>
              <p className="font-body text-gray-500 text-base leading-loose mb-8">
                Your contribution helps us provide access, resources, and opportunities for creatives of all backgrounds — including neurodivergent individuals — who need a safe and structured space to grow.
              </p>

              {/* Impact Tiers */}
              <div className="grid grid-cols-3 gap-3 mb-8">
                {[
                  { amount: '$25', label: 'Supplies one student with art materials' },
                  { amount: '$100', label: 'Funds a youth program scholarship seat' },
                  { amount: '$500', label: 'Sponsors a community workshop' },
                ].map((tier) => (
                  <div key={tier.amount} className="p-4 rounded-xl bg-[#FEF9C3]/60 border border-brand-yellow/30 text-center">
                    <div className="font-heading text-2xl font-black text-brand-yellow-dark">{tier.amount}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:info@creativeinstitutellc.com?subject=Mission%20Giving%20%7C%20Donation"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-colors"
                >
                  <i className="ri-heart-3-fill" /> Give or Donate
                </a>
                <a
                  href="tel:9102424567"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-brand-yellow text-brand-yellow-dark font-body font-semibold text-sm cursor-pointer hover:bg-brand-yellow hover:text-brand-blue-dark transition-colors"
                >
                  <i className="ri-phone-line" /> 910-242-4567
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Contact Banner */}
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} mt-14 max-w-5xl mx-auto p-10 rounded-3xl bg-gradient-to-r from-[#1E3A8A]/5 to-[#5B21B6]/5 border border-brand-purple/10 text-center`}>
          <h3 className="font-heading text-2xl font-black text-brand-blue-dark mb-3">Need Something Else?</h3>
          <p className="font-body text-gray-500 text-base mb-6 max-w-xl mx-auto">
            Looking to book a consultation, request a service, or have a question? We would love to hear from you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-semibold text-sm cursor-pointer hover:opacity-90 transition-opacity">
              <i className="ri-mail-send-line" /> Get in Touch
            </Link>
            <Link to="/enroll" className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-brand-purple text-brand-purple font-body font-semibold text-sm cursor-pointer hover:bg-brand-purple hover:text-white transition-colors">
              <i className="ri-user-add-line" /> Enroll Now
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionQuote() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section ref={ref} className="py-20 bg-gradient-to-r from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} relative z-10 max-w-3xl mx-auto px-8 text-center`}>
        <i className="ri-double-quotes-l text-5xl text-brand-yellow/30 mb-4 block" />
        <p className="font-heading text-white text-2xl lg:text-3xl font-black leading-snug mb-4">
          Where Every Creative Mind Matters.
        </p>
        <p className="font-body text-white/60 text-sm uppercase tracking-widest">— Creative Institute of Fayetteville, NC</p>
      </div>
    </section>
  );
}

export default function ClientPortalPage() {
  return (
    <main>
      <PageHero />
      <PaymentMissionSection />
      <MissionQuote />
    </main>
  );
}
